/**
 * TasksAdmin.tsx — Admin > Tasks > Tasks.
 *
 * The corporate dashboard's Tasks page for the signed-in account: the task
 * templates task groups are built from, in the Lead Sources layout (see
 * components/admin/AdminList.tsx):
 *   - Task Name, Type (corp's coloured badge) and Groups (how many task groups
 *     use it; click for their names), by name; search by name, filter by type,
 *     Show deleted;
 *   - "Add task" top right; a click on a row opens it; Edit / Delete on a live
 *     task (Delete greyed out while an active task group uses it, as corp),
 *     Restore on a deleted one;
 *   - the form is TaskFormModal; the task groups that use a task open from it.
 *
 * On a Locked page (useAdminPageLevel) nothing changes and nothing says why
 * (R8, R18): no Add, row icons or Actions column, and no wording that invites
 * a change; a task (and a group that uses it) still opens, read-only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Layers, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { CountPill } from '@/components/admin/CorpBadges';
import { ListFilterBar } from '@/components/admin/ListFilterBar';
import { TaskFormModal, TaskTypeBadge, type TaskGroupUse } from '@/components/admin/TaskFormModal';
import { TaskGroupFormModal } from '@/components/admin/TaskGroupFormModal';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import { listAdminTaskGroups, type AdminTaskGroup } from '@/services/taskGroupsAdmin.service';
import { deleteAdminTask, listAdminTasks, restoreAdminTask, taskTypeLabel, type AdminTask } from '@/services/tasksAdmin.service';
import { toast } from '@/store/toastStore';

const COLUMNS: AdminListColumn[] = [
  { key: 'name', header: 'Task Name', className: 'w-[40%]', sortable: true },
  { key: 'type', header: 'Type', sortable: true },
  { key: 'groups', header: 'Groups', className: 'w-24' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

function taskName(t: AdminTask): string {
  return t.name.trim() || `Task #${t.taskID}`;
}

const SORT: ListSortCompare<AdminTask> = {
  name: (a, b) => compareText(taskName(a), taskName(b)),
  type: (a, b) => compareText(taskTypeLabel(a.autoTaskID), taskTypeLabel(b.autoTaskID)),
};

/** The task groups that use each task. */
function groupUses(groups: AdminTaskGroup[]): Map<number, TaskGroupUse[]> {
  const out = new Map<number, TaskGroupUse[]>();
  for (const g of groups) {
    for (const item of g.items) {
      if (!item.taskID) continue;
      const list = out.get(item.taskID) ?? [];
      if (!list.some((u) => u.taskGroupID === g.taskGroupID)) list.push({ taskGroupID: g.taskGroupID, name: g.name, active: g.active });
      out.set(item.taskID, list);
    }
  }
  for (const list of out.values()) list.sort((a, b) => compareText(a.name, b.name));
  return out;
}

function GroupsPill({ uses }: { uses: TaskGroupUse[] }) {
  if (uses.length === 0) return <CountPill tone="gray" icon={<Layers />} count={0} hideZero={false} title="Not used in any task group" />;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Used in ${uses.length} task group${uses.length === 1 ? '' : 's'}`}
          onClick={(e) => e.stopPropagation()}
          className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <CountPill tone="blue" icon={<Layers />} count={uses.length} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" onClick={(e) => e.stopPropagation()}>
        <p className="mb-2 text-xs font-semibold text-slate-600">Used in groups</p>
        <ul className="space-y-1 text-sm text-slate-700">
          {uses.map((u) => (
            <li key={u.taskGroupID} className={cn(!u.active && 'text-slate-400 line-through')}>
              {u.name}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

export function TasksAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [tasks, setTasks] = useState<AdminTask[]>([]);
  const [groups, setGroups] = useState<AdminTaskGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [types, setTypes] = useState<string[]>([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<AdminTask | 'new' | null>(null);
  const [editingGroupID, setEditingGroupID] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<AdminTask | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const [taskList, groupList] = await Promise.all([listAdminTasks(), listAdminTaskGroups().catch(() => [] as AdminTaskGroup[])]);
      setTasks(taskList);
      setGroups(groupList);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load tasks'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const uses = useMemo(() => groupUses(groups), [groups]);
  const inActiveGroup = (taskID: number) => (uses.get(taskID) ?? []).some((u) => u.active);

  const typeOptions = useMemo(() => {
    const present = new Set(tasks.map((t) => taskTypeLabel(t.autoTaskID)));
    return [...present].sort(compareText).map((label) => ({ value: label, label }));
  }, [tasks]);

  const query = search.trim().toLowerCase();
  const filtering = query !== '' || types.length > 0;
  const rows = useMemo(
    () =>
      tasks.filter(
        (t) =>
          (showDeleted || t.active) &&
          (query === '' || t.name.toLowerCase().includes(query)) &&
          (types.length === 0 || types.includes(taskTypeLabel(t.autoTaskID))),
      ),
    [tasks, showDeleted, query, types],
  );
  const sorting = useListSort(rows, { initialKey: 'name', compare: SORT });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, types, showDeleted] });

  async function handleDelete() {
    if (!deleting) return;
    const { taskID } = deleting;
    const name = taskName(deleting);
    try {
      await deleteAdminTask(taskID);
      setTasks((prev) => prev.map((t) => (t.taskID === taskID ? { ...t, active: false } : t)));
      toast.success(`"${name}" deleted`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The task could not be deleted.'));
    }
  }

  async function handleRestore(t: AdminTask) {
    try {
      await restoreAdminTask(t.taskID);
      setTasks((prev) => prev.map((x) => (x.taskID === t.taskID ? { ...x, active: true } : x)));
      toast.success('Task restored');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The task could not be restored.'));
    }
  }

  return (
    <AdminListPage
      title="Tasks"
      subtitle={locked ? 'The tasks your task groups are built from.' : 'Manage the tasks your task groups are built from.'}
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add task
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search tasks..."
          searchLabel="Search tasks"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="tasks-show-deleted"
        >
          <ListFilterBar
            filters={[
              {
                id: 'tasks-type-filter',
                label: 'Filter by type',
                placeholder: 'All Types',
                searchPlaceholder: 'Search types...',
                emptyMessage: 'No types found.',
                options: typeOptions,
                selected: types,
                onChange: setTypes,
              },
            ]}
          />
        </AdminListToolbar>
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading tasks…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={filtering ? 'No tasks found' : 'No tasks yet'}
          hint={filtering ? 'Try adjusting your search or filters.' : locked ? undefined : 'Add a task to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Tasks" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((t) => {
              const deleted = !t.active;
              const name = taskName(t);
              const blocked = !deleted && inActiveGroup(t.taskID);
              return (
                <tr key={t.taskID} data-task-id={t.taskID} className={adminRowClass({ deleted, clickable: true })} onClick={() => setEditing(t)}>
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName deleted={deleted} nameTestId="task-name">
                      {name}
                    </AdminRowName>
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    <TaskTypeBadge autoTaskID={t.autoTaskID} />
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    <GroupsPill uses={uses.get(t.taskID) ?? []} />
                  </td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setEditing(t)} />
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore(t)} />
                        ) : (
                          <AdminRowAction
                            kind="delete"
                            label={`Delete ${name}`}
                            blockedReason={blocked ? 'Attached to an active task group' : null}
                            onClick={() =>
                              blocked ? toast.error('This task is attached to an active task group and cannot be deleted.') : setDeleting(t)
                            }
                          />
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['task', 'tasks']} />
        </>
      )}

      {editing && (
        <TaskFormModal
          key={editing === 'new' ? 'new' : editing.taskID}
          task={editing === 'new' ? null : editing}
          groups={editing === 'new' ? [] : (uses.get(editing.taskID) ?? [])}
          onEditGroup={setEditingGroupID}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}

      {editingGroupID !== null && (
        <TaskGroupFormModal
          key={editingGroupID}
          taskGroupID={editingGroupID}
          tasks={tasks}
          onClose={() => setEditingGroupID(null)}
          onSaved={() => {
            setEditingGroupID(null);
            void load(true);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete "${taskName(deleting)}"?` : ''}
        message="It can no longer be added to task groups. You can restore it from Show deleted."
        confirmLabel="Delete task"
        variant="danger"
      />
    </AdminListPage>
  );
}
